import CDNIcon from '@alfa-retail-react/components/cdn-icon';
import SuperEllipse from '@alfa-retail-react/components/super-ellipse';
import { observer } from 'mobx-react';
import React, { Ref, useEffect, useRef } from 'react';
import { FormProvider, UseFormReturn } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import {
    MainScreen,
    OverContentLoadableBlock,
    PageFrame,
    Section,
    ShowComponent,
    useForm,
    useNavigate,
} from '@alfa-retail-react/components';
import Button from '@alfa-retail-react/components/button';

import { FraudNotification } from 'app/logic/product/FraudNotification';
import { HowItWorks } from 'app/logic/pages/introduction/HowItWorks';
import { Step } from 'app/component/step/Step';
import { ProductRequirements } from 'app/logic/product/ProductRequirements';
import { IntroProductAdvantages } from 'app/logic/product/IntroProductAdvantages';
import { PoliticsOfConfindential } from 'app/logic/pages/introduction/PoliticsOfConfindential';
import { AnalyticsActionTypeName } from 'app/analytics/AnalyticsActionTypeName';
import { useAnalytics } from 'app/analytics/useAnalytics';
import { useApplicationContext } from 'app/store/сontext/ApplicationContext';
import SessionStorageTokenStore from 'app/auth/SessionStorageTokenStore';
import { IntroductionSkeleton } from 'app/component/page/skeletons';
import {
    STEP_PROGRESS_PERCENT,
    StepOrder,
    TOTAL_STEP_COUNT,
} from 'app/constants/step';
import { useNotificationContext } from 'app/store/сontext/NotificationContextProvider';
import { defaultExceptionHandler } from 'app/logic/pages/error/defaultExceptionHandler';
import { exceptionLogger } from 'app/logic/pages/error/exceptionLogger';
import {
    networkExceptionHandlerOnLoadData,
    networkExceptionHandlerOnSubmitData,
} from 'app/logic/pages/error/networkExceptionHandler';
import { orderRejectedExceptionHandler } from 'app/logic/pages/error/orderRejectedExceptionHandler';
import { unauthorizedExceptionHandlerOnLoadData } from '../error/unauthorizedExceptionHandler';
import { PageErrors } from 'app/logic/pages/error/PageErrors';
import { validationExceptionHandler } from 'app/logic/pages/error/validationExceptionHandler';
import { IntroductionPageStore } from 'app/logic/pages/introduction/IntroductionPageStore';
import { PersonalDataForm } from 'app/logic/pages/introduction/PersonalDataForm';
import { CardProductView } from 'app/logic/product/CardProductView';
import { CardOrder } from 'app/model/CardOrder';
import { Client } from 'app/model/Client';
import { OrderService } from 'app/service/OrderService';
import { StepNames } from 'app/StepNames';
import { useI18nContext } from 'app/store/сontext/I18nContext';
import { CreditCalculator } from './CreditCalculator/CreditCalculator';
import { PHONE_FORM_ITEM_NAME } from './PhoneFormItem';

import 'app/logic/pages/introduction/IntroductionPage.less';

export interface IntroductionFormValues {
    phone: string;
    identityNumber?: string;
}

const CURRENT_STEP_ORDER: number = StepOrder.STEP_VIEW_PRODUCT_DATA;
const PAGE_NAME = 'ProductPage';
const DEFAULT_MIN_INSURANCE_AMOUNT = 1000;
const SUBMIT_LABEL_ADDON =
    'Мы гарантируем безопасность и сохранность ваших данных';

const IntroductionPageContent = observer(
    ({ pageStore }: { pageStore: IntroductionPageStore }) => {
        const applicationContext = useApplicationContext();
        const navigate = useNavigate();
        const notification = useNotificationContext();
        const analytics = useAnalytics(
            applicationContext.productCode,
            PAGE_NAME,
            false,
        );
        const orderService = useRef(new OrderService()).current;
        const i18nContext = useI18nContext();

        const {
            cardProduct,
            creditCalculator,
            creditCalculatorSum,
            setCreditCalculatorSum,
            loadStateStore,
            loadStateCalculator,
            formStore,
        } = pageStore;
        const isKKG = cardProduct?.code === 'KKG';

        const form = useForm<IntroductionFormValues>({
            errorContextConverter: pageStore.contextConverter,
        });

        const {
            handleSubmitWithValidationError,
            pageErrors,
            setPageErrors,
            pageErrorsComponentRef,
        } = form;

        useEffect(() => {
            analytics.pageDisplayed();
        }, []);

        const stepperHint = isKKG ? (
            <>
                Станьте на{' '}
                <span className="progress">+{STEP_PROGRESS_PERCENT}%</span>{' '}
                ближе к карте - заполните поле «Номер телефона»
            </>
        ) : (
            <>
                Вероятность одобрения карты{' '}
                <span className="progress">+{STEP_PROGRESS_PERCENT}%</span> за
                1-й шаг
            </>
        );

        const stepper = (
            <Step
                regularHint={isKKG}
                hint={stepperHint}
                current={CURRENT_STEP_ORDER}
                total={TOTAL_STEP_COUNT}
            />
        );

        const onSubmitForm = async () => {
            // const identityDocument = IdentityDocument.withPersonalNumber(formStore.personalNumber ?? null);
            const identityDocument: null = null;
            const client = Client.withPersonalData(
                identityDocument,
                formStore.phone,
            );

            const order = CardOrder.create(
                cardProduct.code,
                cardProduct.id,
                applicationContext.channel,
                null,
                client,
                null,
            );

            order.stage = StepNames.STEP_VIEW_PRODUCT_DATA;

            return pageStore.actionStateStore.loadData(async () => {
                analytics.confirm();

                return orderService
                    .createOrder(order)
                    .then((cardOrder) => {
                        applicationContext.latestSubmittedStep =
                            CURRENT_STEP_ORDER;
                        applicationContext.internalId = cardOrder.number;
                        applicationContext.phoneNumber =
                            cardOrder.client.mobilePhone;

                        analytics.resolve(AnalyticsActionTypeName.CONFIRM);

                        sessionStorage.setItem('orderId', cardOrder.id);
                        navigate(
                            `/${applicationContext.productCode}/draft?orderId=${cardOrder.id}&channel=${cardOrder.saleChannel}`,
                            { refererPage: PAGE_NAME },
                        );
                    })
                    .catch(
                        exceptionLogger(PAGE_NAME, 'orderService.createOrder'),
                    )
                    .catch(
                        orderRejectedExceptionHandler(
                            navigate,
                            analytics,
                            i18nContext.t,
                        ),
                    )
                    .catch(
                        validationExceptionHandler(
                            form,
                            analytics,
                            pageStore.contextConverter,
                        ),
                    )
                    .catch(
                        networkExceptionHandlerOnSubmitData({ setPageErrors }),
                    )
                    .catch(defaultExceptionHandler(notification, analytics));
            });
        };

        const handleSumbitFormWithErrors = () => {
            if (!form.getFieldState(PHONE_FORM_ITEM_NAME).invalid) {
                onSubmitForm();
            }
        };

        const formElements = (
            <>
                {stepper}
                <PersonalDataForm formStore={formStore} />
                {pageErrors.length > 0 && (
                    <PageErrors
                        ref={pageErrorsComponentRef as Ref<HTMLDivElement>}
                        errors={pageErrors}
                    />
                )}
            </>
        );

        const advantagesElement = (
            <IntroProductAdvantages
                cardProduct={pageStore.cardProduct}
                minAmount={
                    formStore?.insuranceStore.minInsuranceCreditAmount ||
                    DEFAULT_MIN_INSURANCE_AMOUNT
                }
            />
        );

        const fraudNotificationElement = <FraudNotification />;
        const requirementsElement = <ProductRequirements />;

        const introPageData = isKKG ? (
            <>
                {advantagesElement}
                {fraudNotificationElement}
                {requirementsElement}
                <Section
                    title={applicationContext.getCardProductTitle()}
                    className="IntroProductAdvantages"
                >
                    {formElements}
                </Section>
            </>
        ) : (
            <>
                {formElements}
                {advantagesElement}
                <HowItWorks
                    title={`Как работает ${cardProduct?.name}?`}
                    analytics={analytics}
                />
                {!loadStateCalculator.hasError &&
                    (window as any).env.FEATURE_FLAGS?.creditCalculator &&
                    !!creditCalculator && (
                        <CreditCalculator
                            loadState={loadStateCalculator}
                            creditCalculator={creditCalculator}
                            sum={creditCalculatorSum}
                            onChangeSum={setCreditCalculatorSum}
                        />
                    )}
                {fraudNotificationElement}
                {requirementsElement}
            </>
        );

        const submitButton = (
            <Button
                className="submitAction"
                type="submit"
                view="accent"
                size="m"
                disabled={pageStore.actionStateStore.isLoading}
            >
                Продолжить
            </Button>
        );

        const submitBlock = isKKG ? (
            <div className="submitBlock">
                <div className="submitBlockAddon">
                    <SuperEllipse size={48} border className="addonIcon">
                        <CDNIcon name="glyph_shield_m" color="black" />
                    </SuperEllipse>
                    <span>{SUBMIT_LABEL_ADDON}</span>
                </div>
                {submitButton}
            </div>
        ) : (
            submitButton
        );

        return (
            <div className="introductionPage">
                <ShowComponent
                    condition={loadStateStore.isLoaded}
                    alternative={<IntroductionSkeleton />}
                >
                    <>
                        <CardProductView
                            pageStore={pageStore}
                            order={CardOrder.default()}
                        />
                        <FormProvider<
                            IntroductionFormValues,
                            UseFormReturn<IntroductionFormValues>
                        >
                            {...form}
                        >
                            <form
                                onSubmit={handleSubmitWithValidationError({
                                    onInvalid: handleSumbitFormWithErrors,
                                    onValid: onSubmitForm,
                                })}
                            >
                                {introPageData}
                                {submitBlock}
                            </form>
                        </FormProvider>
                        <PoliticsOfConfindential />
                    </>
                </ShowComponent>
            </div>
        );
    },
);

/**
 * Вводная страница/страница регистрации пользователя
 */
export const IntroductionPage = observer(() => {
    const applicationContext = useApplicationContext();
    const params = useParams();
    const pageStore = useRef(new IntroductionPageStore()).current;
    const analytics = useAnalytics(
        applicationContext.productCode,
        PAGE_NAME,
        false,
    );
    const notification = useNotificationContext();
    const navigate = useNavigate();
    const i18nContext = useI18nContext();

    useEffect(() => {
        SessionStorageTokenStore.clear();

        pageStore
            .initialize(params.productCode, applicationContext.channel)
            .then(() => {
                applicationContext.cardProduct = pageStore.cardProduct;
            })
            .catch(exceptionLogger(PAGE_NAME, 'pageStore.initialize'))
            .catch(networkExceptionHandlerOnLoadData({ navigate }))
            .catch(defaultExceptionHandler(notification, analytics))
            .then(() => {
                pageStore
                    .initializeCalculator()
                    ?.catch(
                        exceptionLogger(
                            PAGE_NAME,
                            'pageStore.initializeCalculator',
                        ),
                    )
                    ?.catch(defaultExceptionHandler(notification, analytics));
            });

        i18nContext
            .initialize()
            .catch(
                unauthorizedExceptionHandlerOnLoadData({
                    navigate,
                    phoneNumber: applicationContext.phoneNumber,
                }),
            )
            .catch(exceptionLogger(PAGE_NAME, 'i18nContext.initialize'))
            .catch(networkExceptionHandlerOnLoadData({ navigate }));
    }, []);

    const { cardProduct } = pageStore;
    const isKKG = cardProduct?.code === 'KKG';

    return (
        <OverContentLoadableBlock show={pageStore.actionStateStore.isLoading}>
            <PageFrame
                rightAddonLogo={applicationContext.additionalLogo}
                displayFooter
            >
                <MainScreen>
                    <MainScreen.Header
                        title={applicationContext.getCardProductTitle(
                            cardProduct?.name,
                            isKKG ? 'Условия по' : null,
                        )}
                    />
                    <MainScreen.ContentBar>
                        <IntroductionPageContent pageStore={pageStore} />
                    </MainScreen.ContentBar>
                </MainScreen>
            </PageFrame>
        </OverContentLoadableBlock>
    );
});
